import { warnWithTrace } from '../helpers/logger';
import { deprecations } from './deprecations';
import { getDesignTokenValue, isFluidDesignTokenValue, } from './get-design-token-value';
import { themeSet as defaultThemeSet } from './example-theme-definitions';
/**
 * TypeGuard for type `ThemeName`
 *
 * @param prop Some arbitrary component property
 */
function isThemeName(prop) {
    return typeof prop === 'string';
}
function warnIfDeprecated(path) {
    deprecations.forEach((deprecation) => {
        if (path.match(deprecation.condition)) {
            warnWithTrace(`found: ${path} - ${deprecation.message}`);
        }
    });
}
function addProxy(targetObject, getFallbackTheme = () => undefined, path) {
    return new Proxy(targetObject, {
        get: (target, key) => {
            if (typeof key !== 'string') {
                return undefined;
            }
            if (Object.hasOwnProperty.call(target, key)) {
                warnIfDeprecated(path ? `${path}.${key}` : `${key}`);
                return target[key];
            }
            const fallbackTheme = getFallbackTheme();
            return fallbackTheme && fallbackTheme[key];
        },
    });
}
function recursivelyAddProxies(targetObject, getFallbackTheme = () => undefined, path) {
    const result = {};
    Object.entries(targetObject).forEach(([key, value]) => {
        if (typeof value === 'object' && value) {
            result[key] = recursivelyAddProxies(value, () => {
                const fallbackTheme = getFallbackTheme();
                return fallbackTheme && fallbackTheme[key];
            }, path ? `${path}.${key}` : key);
        }
        else {
            result[key] = value;
        }
    });
    return addProxy(result, getFallbackTheme, path);
}
function isThemeSubtree(subtree) {
    return (!Array.isArray(subtree) &&
        typeof subtree !== 'string' &&
        !isFluidDesignTokenValue(subtree));
}
function resolveDesignTokenValues(themeSubTree, tokensOverride) {
    const resolvedTheme = {};
    Object.entries(themeSubTree).forEach(([key, value]) => {
        if (value === null || value === undefined) {
            return;
        }
        if (isThemeSubtree(value)) {
            resolvedTheme[key] =
                resolveDesignTokenValues(value, tokensOverride) || {};
            return;
        }
        if (isFluidDesignTokenValue(value)) {
            resolvedTheme[key] = {
                min: getDesignTokenValue(value.min, tokensOverride),
                max: getDesignTokenValue(value.max, tokensOverride),
            };
            return;
        }
        resolvedTheme[key] = getDesignTokenValue(value, tokensOverride);
    });
    return resolvedTheme;
}
function addLanguageAndDirectionToTheme(theme, language, direction) {
    // avoid empty keys in themes
    let result = theme;
    if (language) {
        result = Object.assign(Object.assign({}, result), { language });
    }
    if (direction) {
        result = Object.assign(Object.assign({}, result), { direction });
    }
    return result;
}
export function getTheme(currentTheme, newTheme, language, direction, optionsOverride) {
    const optionsThemeSet = optionsOverride
        ? optionsOverride.themeSet
        : undefined;
    const themeSet = optionsThemeSet || defaultThemeSet;
    const parentTheme = currentTheme && Object.keys(currentTheme).length > 0
        ? currentTheme
        : recursivelyAddProxies(resolveDesignTokenValues(themeSet.main, optionsOverride && optionsOverride.tokens));
    if (newTheme && isThemeName(newTheme) && newTheme === 'image') {
        warnWithTrace('The `image` theme is deprecated. Please use the `inverted` theme instead.');
    }
    const theme = (newTheme && isThemeName(newTheme) ? themeSet[newTheme] : newTheme) || {};
    const themeWithResolvedDesignTokens = resolveDesignTokenValues(theme, optionsOverride && optionsOverride.tokens);
    const themeWithLanguageAndDirection = addLanguageAndDirectionToTheme(themeWithResolvedDesignTokens, language, direction);
    return recursivelyAddProxies(themeWithLanguageAndDirection, () => parentTheme);
}
